import apiClient from '../config/api';
import dashboardService from './dashboardService';

/** Client-only hints; must never persist across tenants — nav scope comes from GET /dashboard/nav-app-scope */
export function stripTenantModuleHints(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const { appCodes, subscribedModules, modules, ...rest } = obj;
  return rest;
}

/** sessionStorage entries that are per-tenant; must not survive a new login */
export function clearTenantScopedSessionCaches() {
  if (typeof window === 'undefined') return;
  [
    'pm_nav_scope_v2',
    'dashboard_usage_summary',
    'dashboard_usage_summary_timestamp',
    'common_data_timestamp',
    'plants_data',
    'users_data',
    'products_data',
    'machines_data',
    'lines_data',
    'shifts_data',
  ].forEach((k) => sessionStorage.removeItem(k));
}

const authService = {
  /**
   * Login user
   * @param {string} username - Username
   * @param {string} password - Password
   * @param {number} tenantId - Tenant ID
   * @returns {Promise} Login response with token and user info
   */
  login: async (username, password, tenantId) => {
    const response = await apiClient.post('/auth/login', {
      username,
      password,
      tenantId,
    });

    // Store token and user info in localStorage
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem(
        'tenant',
        response.data.tenant ? JSON.stringify(stripTenantModuleHints(response.data.tenant)) : '',
      );
      localStorage.setItem('tenantId', response.data.tenant?.tenantId?.toString() || '');
      clearTenantScopedSessionCaches();
      dashboardService.clearNavScopeMemoryCache();
      // Drop any in-flight nav-scope response from the previous session/tenant
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('pm_nav_scope_invalidate'));
      }
      // pm_session_identity_changed is dispatched from Login.js inside flushSync so the nav provider
      // remounts before React paints /home (otherwise the first frame still shows the previous tenant).
      // Persist plant access info
      localStorage.setItem('plantIds', JSON.stringify(response.data.user?.plantIds || []));
      localStorage.setItem('defaultPlantId', response.data.user?.defaultPlantId?.toString() || '');
    }

    return response.data;
  },

  /**
   * Logout user
   */
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenant');
    localStorage.removeItem('plantIds');
    localStorage.removeItem('defaultPlantId');
    clearTenantScopedSessionCaches();
    dashboardService.clearNavScopeMemoryCache();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pm_nav_scope_invalidate'));
      window.dispatchEvent(new CustomEvent('pm_session_identity_changed'));
    }
  },

  /**
   * Get current user info
   * @returns {Promise} Current user information
   */
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  /**
   * Validate token
   * @param {string} token - JWT token
   * @returns {Promise} Validation result
   */
  validateToken: async (token) => {
    const response = await apiClient.post('/auth/validate', { token });
    return response.data;
  },

  /**
   * Check if user is authenticated and token is not expired
   * @returns {boolean} True if token exists and is valid
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;

    try {
      // Decode JWT payload to check expiration
      const payloadBase64 = token.split('.')[1];
      if (!payloadBase64) return false;

      const payload = JSON.parse(atob(payloadBase64));
      const now = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < now) {
        // Token expired, clear it
        authService.logout();
        return false;
      }

      return true;
    } catch (e) {
      console.error('Error parsing token:', e);
      return false;
    }
  },

  /**
   * Check if a specific token is expired
   * @param {string} token 
   * @returns {boolean}
   */
  isTokenExpired: (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp < Math.floor(Date.now() / 1000);
    } catch (e) {
      return true;
    }
  },

  /**
   * Get stored auth token
   * @returns {string|null} Auth token or null
   */
  getToken: () => {
    return localStorage.getItem('authToken');
  },

  /**
   * Get stored user info
   * @returns {object|null} User object or null
   */
  getUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Get stored user ID
   * @returns {number|null} User ID or null
   */
  getUserId: () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    const user = JSON.parse(userStr);
    return user?.userId || user?.UserId || null;
  },

  /**
   * Get stored tenant ID
   * @returns {number|null} Tenant ID or null
   */
  getTenantId: () => {
    const tenantId = localStorage.getItem('tenantId');
    return tenantId ? parseInt(tenantId, 10) : null;
  },

  /**
   * Get plant IDs this user is allowed to access
   * @returns {number[]} Array of plant IDs
   */
  getAssignedPlantIds: () => {
    try {
      const raw = localStorage.getItem('plantIds');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  },

  /**
   * Get the user's default plant ID
   * @returns {number|null}
   */
  getDefaultPlantId: () => {
    const raw = localStorage.getItem('defaultPlantId');
    return raw ? parseInt(raw, 10) : null;
  },

  /**
   * Get stored tenant info
   * @returns {object|null} Tenant object or null
   */
  getTenantInfo: () => {
    const tenantStr = localStorage.getItem('tenant');
    return tenantStr ? JSON.parse(tenantStr) : null;
  },

  /**
   * Request password reset token
   * @param {string} username - Username
   * @returns {Promise} Response with reset token
   */
  requestPasswordReset: async (username) => {
    const response = await apiClient.post('/auth/forgot-password', {
      username,
    });
    return response.data;
  },

  /**
   * Reset password using token
   * @param {string} username - Username
   * @param {string} resetToken - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise} Response with success status
   */
  resetPassword: async (username, resetToken, newPassword) => {
    const response = await apiClient.post('/auth/reset-password', {
      username,
      resetToken,
      newPassword,
    });
    return response.data;
  },

  /**
   * Change password for logged-in user
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise} Response with success status
   */
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  /**
   * Update tenant information
   * @param {number} tenantId - Tenant ID
   * @param {object} tenantData - Updated tenant data
   * @returns {Promise} Updated tenant data
   */
  /**
   * Update tenant information
   * @param {number} tenantId - Tenant ID
   * @param {object} tenantData - Updated tenant data
   * @returns {Promise} Updated tenant data
   */
  updateTenant: async (tenantId, tenantData) => {
    const response = await apiClient.post(`/tenant/update/${tenantId}`, tenantData);

    // Update local storage if successful
    if (response.data && response.data.tenantId) {
      const currentTenant = stripTenantModuleHints(
        JSON.parse(localStorage.getItem('tenant') || '{}'),
      );
      const updatedTenant = {
        ...currentTenant,
        tenantId: response.data.tenantId,
        tenantName: response.data.tenantName,
        tenantCode: response.data.tenantCode,
        email: response.data.email,
        contactPerson: response.data.contactPerson,
        phone: response.data.phone,
        address: response.data.address,
        industry: response.data.industry,
        logoUrl: response.data.logoUrl || currentTenant.logoUrl,
        subscriptionStatus: response.data.subscriptionStatus,
      };
      localStorage.setItem('tenant', JSON.stringify(updatedTenant));
    }
    return response.data;
  },

  /**
   * Get tenant by ID
   * @param {number} tenantId - Tenant ID
   * @returns {Promise} Tenant data
   */
  getTenant: async (tenantId) => {
    const response = await apiClient.get(`/tenant/${tenantId}`);
    return response.data;
  },

  /**
   * Upload tenant logo
   * @param {number} tenantId - Tenant ID
   * @param {File} file - Logo file
   * @returns {Promise} Response with new logo URL
   */
  uploadLogo: async (tenantId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/tenant/upload-logo/${tenantId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Update local storage
    if (response.data && response.data.logoUrl) {
      const currentTenant = stripTenantModuleHints(
        JSON.parse(localStorage.getItem('tenant') || '{}'),
      );
      const updatedTenant = {
        ...currentTenant,
        logoUrl: response.data.logoUrl,
      };
      localStorage.setItem('tenant', JSON.stringify(updatedTenant));
    }

    return response.data;
  },
};

export default authService;

